import React, { useState, useEffect, useCallback } from 'react';
import { automationAPI } from '../../services/api';
import { toast } from '../../utils/toast';
import Icon from '../../ui/Icon';

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 16, fontWeight: 800, color: '#1A202C' },
  sub: { fontSize: 12, color: '#718096', marginTop: 4, lineHeight: 1.5 },
  banner: {
    background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10,
    padding: '12px 16px', fontSize: 12, color: '#1E40AF', marginBottom: 16, lineHeight: 1.55,
  },
  btn: (v) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
    background: v === 'primary' ? 'var(--accent)' : v === 'danger' ? '#FEF2F2' : v === 'warn' ? '#FEF3C7' : v === 'success' ? '#DCFCE7' : '#F1F5F9',
    color: v === 'primary' ? '#fff' : v === 'danger' ? '#EF4444' : v === 'warn' ? '#92400E' : v === 'success' ? '#166534' : '#374151',
  }),
  input: {
    width: '100%', padding: '9px 11px', border: '1.5px solid #E2E8F0', borderRadius: 8,
    fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    background: '#FAFAFA', color: '#1A202C',
  },
  label: {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
  },
  th: {
    fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase',
    letterSpacing: '0.07em', padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
    background: '#F8FAFF', textAlign: 'left',
  },
  td: {
    fontSize: 13, color: '#374151', padding: '11px 14px',
    borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle',
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modal: {
    background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
    maxHeight: '90vh', overflow: 'auto', padding: '28px 28px 24px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
};

function StatusBadge({ enabled }) {
  const ok = enabled !== false;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: ok ? '#DCFCE7' : '#FEE2E2', color: ok ? '#166534' : '#991B1B',
    }}>
      {ok ? 'Enabled' : 'Disabled'}
    </span>
  );
}

async function queueAction(action, params, label) {
  const result = await automationAPI.submitForApproval(action, params);
  toast(`${label} queued — ${result.approvalNumber}. Nothing runs until approved in M365 Automation.`, 'success');
  return result;
}

function parseGroupList(text) {
  return String(text || '')
    .split(/[\n,;]+/)
    .map(g => g.trim())
    .filter(Boolean);
}

function ModalShell({ title, subtitle, onClose, children, footer }) {
  return (
    <div style={s.modalOverlay}>
      <div style={s.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C' }}>{title}</div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <Icon name="close" size={18} color="#9CA3AF" />
          </button>
        </div>
        {subtitle && <p style={{ fontSize: 12, color: '#718096', marginBottom: 20, lineHeight: 1.5 }}>{subtitle}</p>}
        {children}
        {footer}
      </div>
    </div>
  );
}

function ScopeBanner({ scope }) {
  if (!scope?.enforced) return null;
  return (
    <div style={s.banner}>
      <strong>Scoped automation active.</strong>
      {' '}Only approved users and groups can be changed. Operators: {scope.operators?.length || 0} account(s).
      <br />
      <strong>Allowed groups:</strong> {(scope.allowedGroups || []).join(', ')}
      <br />
      <strong>Allowed users:</strong> {(scope.allowedUsers || []).join(', ')}
      <br />
      <strong>New users:</strong> Create/Onboard automatically adds{' '}
      {(scope.defaultOnboardGroups || []).join(', ')} after approval.
    </div>
  );
}

function CreateUserModal({ onClose, onSubmitted, scope }) {
  const [form, setForm] = useState({
    displayName: '', userPrincipalName: '', givenName: '', surname: '',
    jobTitle: '', department: '', mobilePhone: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.displayName.trim() || !form.userPrincipalName.trim()) {
      toast('Full name and company email are required', 'error');
      return;
    }
    if (!window.confirm('Submit CREATE USER request for approval?\n\nNo account is created until M365 Automation approves.')) return;
    setBusy(true);
    try {
      await queueAction('CREATE_USER', {
        displayName: form.displayName.trim(),
        userPrincipalName: form.userPrincipalName.trim().toLowerCase(),
        givenName: form.givenName.trim() || undefined,
        surname: form.surname.trim() || undefined,
        jobTitle: form.jobTitle.trim() || undefined,
        department: form.department.trim() || undefined,
        mobilePhone: form.mobilePhone.trim() || undefined,
      }, 'Create user');
      onSubmitted();
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, opts = {}) => (
    <div>
      <label style={s.label}>{label}{opts.required ? ' *' : ''}</label>
      <input
        type={opts.type || 'text'}
        style={s.input}
        value={form[key]}
        placeholder={opts.placeholder}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      />
    </div>
  );

  return (
    <ModalShell
      title="Request create M365 user"
      subtitle="Submits an approval request only. On approval, the account is created and added to all default groups (Finance-Users, VPN-Users, Company-All, Finance-Reporting)."
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button type="button" style={s.btn()} onClick={onClose}>Cancel</button>
          <button type="button" style={s.btn('primary')} onClick={submit} disabled={busy}>
            {busy ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {field('displayName', 'Full name', { required: true, placeholder: 'John Tan' })}
        {field('userPrincipalName', 'Company email (UPN)', { required: true, type: 'email', placeholder: 'john.tan@company.com' })}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('givenName', 'First name', { placeholder: 'John' })}
          {field('surname', 'Last name', { placeholder: 'Tan' })}
        </div>
        {field('jobTitle', 'Job title', { placeholder: 'Support Engineer' })}
        {field('department', 'Department', { placeholder: 'IT Support' })}
        {field('mobilePhone', 'Mobile phone', { type: 'tel', placeholder: '+65 9123 4567' })}
      </div>
    </ModalShell>
  );
}

function OnboardUserModal({ licenses, onClose, onSubmitted, scope }) {
  const defaultGroups = (scope?.defaultOnboardGroups || [
    'Finance-Users', 'VPN-Users', 'Company-All', 'Finance-Reporting',
  ]).join('\n');
  const [form, setForm] = useState({
    displayName: '', userPrincipalName: '', givenName: '', surname: '',
    jobTitle: '', department: '', officeLocation: '', mobilePhone: '',
    licenseSkuId: '', emailGroups: defaultGroups, grantVpn: false,
    welcomeEmailTo: '', managerName: '', sendWelcome: true,
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.displayName.trim() || !form.userPrincipalName.trim()) {
      toast('Full name and company email are required', 'error');
      return;
    }
    if (!window.confirm('Submit ONBOARD USER request for approval?\n\nNothing runs in Microsoft 365 until approved.')) return;
    setBusy(true);
    try {
      const emailGroups = parseGroupList(form.emailGroups);
      const params = {
        displayName: form.displayName.trim(),
        userPrincipalName: form.userPrincipalName.trim().toLowerCase(),
        givenName: form.givenName.trim() || undefined,
        surname: form.surname.trim() || undefined,
        jobTitle: form.jobTitle.trim() || undefined,
        department: form.department.trim() || undefined,
        officeLocation: form.officeLocation.trim() || undefined,
        mobilePhone: form.mobilePhone.trim() || undefined,
        licenseSkuId: form.licenseSkuId || undefined,
        emailGroups: emailGroups.length ? emailGroups : undefined,
        grantVpn: form.grantVpn,
        welcomeEmailTo: form.welcomeEmailTo.trim() || undefined,
        managerName: form.managerName.trim() || undefined,
        sendWelcome: form.sendWelcome,
      };
      await queueAction('ONBOARD_USER', params, 'Onboard user');
      onSubmitted();
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, opts = {}) => (
    <div>
      <label style={s.label}>{label}{opts.required ? ' *' : ''}</label>
      <input
        type={opts.type || 'text'}
        style={s.input}
        value={form[key]}
        placeholder={opts.placeholder}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      />
    </div>
  );

  return (
    <ModalShell
      title="Request onboard new employee"
      subtitle="Approval request only. Create account, license, groups, VPN, welcome email — none run until M365 Automation approves."
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button type="button" style={s.btn()} onClick={onClose}>Cancel</button>
          <button type="button" style={s.btn('primary')} onClick={submit} disabled={busy}>
            {busy ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {field('displayName', 'Full name', { required: true })}
        {field('userPrincipalName', 'Company email (UPN)', { required: true, type: 'email' })}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('givenName', 'First name')}
          {field('surname', 'Last name')}
        </div>
        {field('jobTitle', 'Job title')}
        {field('department', 'Department')}
        {field('officeLocation', 'Office location')}
        {field('mobilePhone', 'Mobile phone', { type: 'tel' })}
        <div>
          <label style={s.label}>License (optional)</label>
          <select
            style={s.input}
            value={form.licenseSkuId}
            onChange={e => setForm(f => ({ ...f, licenseSkuId: e.target.value }))}
          >
            <option value="">Default / none</option>
            {(licenses || []).map(l => (
              <option key={l.skuId} value={l.skuId}>
                {l.skuPartNumber}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={s.label}>Email groups (one per line)</label>
          <textarea
            style={{ ...s.input, minHeight: 72, resize: 'vertical' }}
            value={form.emailGroups}
            placeholder={'Finance-DL\nAll Staff\nVPN-Users'}
            onChange={e => setForm(f => ({ ...f, emailGroups: e.target.value }))}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.grantVpn} onChange={e => setForm(f => ({ ...f, grantVpn: e.target.checked }))} />
          Grant VPN access (uses configured VPN group)
        </label>
        {field('welcomeEmailTo', 'Welcome email to (personal email)', { type: 'email' })}
        {field('managerName', 'Manager name')}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.sendWelcome} onChange={e => setForm(f => ({ ...f, sendWelcome: e.target.checked }))} />
          Send welcome email after approval
        </label>
      </div>
    </ModalShell>
  );
}

export default function M365UsersAdmin() {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState('');
  const [scope, setScope] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await automationAPI.listUsers({ search: search.trim() || undefined, top: 100 });
      setUsers(data.users || []);
      if (data.scope) setScope(data.scope);
    } catch (err) {
      toast(err.message || 'Failed to load users', 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    automationAPI.listLicenses().then(d => setLicenses(d.licenses || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!groupSearch.trim() || groupSearch.length < 2) {
      setGroupOptions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const data = await automationAPI.listGroups({ search: groupSearch.trim(), top: 20 });
        setGroupOptions(data.groups || []);
      } catch {
        setGroupOptions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [groupSearch]);

  const openUser = async (user) => {
    setSelected(user);
    setDetailLoading(true);
    setGroupSearch('');
    setGroupOptions([]);
    setSelectedLicense('');
    try {
      const key = user.userPrincipalName || user.mail || user.id;
      const data = await automationAPI.getUser(key);
      setDetail(data);
    } catch (err) {
      toast(err.message || 'Failed to load user', 'error');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const runAction = async (action, params, label) => {
    if (!window.confirm(`Submit "${label}" for approval?\n\nNo changes until approved in M365 Automation.`)) return;
    setBusy(true);
    try {
      await queueAction(action, params, label);
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const addToGroup = async (group) => {
    const email = selected?.userPrincipalName || selected?.mail;
    const gname = group.displayName || group.mail;
    if (!window.confirm(`Submit ADD to "${gname}" for ${email}?\n\nRequires approval.`)) return;
    setBusy(true);
    try {
      await queueAction('ADD_TO_EMAIL_GROUP', {
        groupName: gname,
        groupId: gname,
        memberEmail: email,
        userKey: email,
      }, 'Add to group');
      setGroupSearch('');
      setGroupOptions([]);
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeFromGroup = async (group) => {
    const email = selected?.userPrincipalName || selected?.mail;
    const gname = group.displayName || group.mail;
    if (!window.confirm(`Submit REMOVE from "${gname}" for ${email}?\n\nRequires approval.`)) return;
    setBusy(true);
    try {
      await queueAction('REMOVE_FROM_EMAIL_GROUP', {
        groupName: gname,
        groupId: gname,
        memberEmail: email,
        userKey: email,
      }, 'Remove from group');
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const assignLicense = async () => {
    const email = selected?.userPrincipalName || selected?.mail;
    if (!selectedLicense) {
      toast('Select a license first', 'error');
      return;
    }
    await runAction('ASSIGN_LICENSE', { userKey: email, skuId: selectedLicense }, 'Assign license');
  };

  const email = selected?.userPrincipalName || selected?.mail;

  return (
    <div>
      <div style={s.header}>
        <div>
          <div style={s.title}>M365 Users</div>
          <div style={s.sub}>Browse, create, and onboard Entra users. Every change goes to M365 Automation for approval first.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={s.btn('success')} onClick={() => setModal('create')}>
            <Icon name="plus" size={14} color="#166534" /> Request create user
          </button>
          <button type="button" style={s.btn('primary')} onClick={() => setModal('onboard')}>
            <Icon name="plus" size={14} color="#fff" /> Request onboard user
          </button>
          <button type="button" style={s.btn()} onClick={load} disabled={loading}>
            <Icon name="refresh" size={14} color="#374151" /> Refresh
          </button>
        </div>
      </div>

      <ScopeBanner scope={scope} />
      <div style={s.banner}>
        <strong>Production safety:</strong> Create, onboard, password reset, groups, license — <strong>only submit requests here</strong>.
        Microsoft 365 is changed <strong>only after approval</strong> in Admin → M365 Automation.
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          style={{ ...s.input, minWidth: 220, width: 'auto' }}
          placeholder="Search name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <button type="button" style={s.btn('primary')} onClick={load}>Search</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: 16 }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>Loading…</div>
          ) : users.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
              {search ? 'No users match your search.' : 'No users found. Use Create User or Onboard User to add a new account.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr>
                    <th style={s.th}>Name</th>
                    <th style={s.th}>Email</th>
                    <th style={s.th}>Department</th>
                    <th style={s.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr
                      key={u.id}
                      style={{ cursor: 'pointer', background: selected?.id === u.id ? '#EFF6FF' : 'transparent' }}
                      onClick={() => openUser(u)}
                    >
                      <td style={{ ...s.td, fontWeight: 600 }}>{u.displayName || '—'}</td>
                      <td style={s.td}>{u.userPrincipalName || u.mail || '—'}</td>
                      <td style={s.td}>{u.department || '—'}</td>
                      <td style={s.td}><StatusBadge enabled={u.accountEnabled} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selected && (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 18, maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>{selected.displayName}</div>
            <div style={{ fontSize: 12, color: '#718096', marginBottom: 14 }}>{email}</div>

            {detailLoading ? (
              <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div>
            ) : detail ? (
              <>
                <div style={{ fontSize: 12, marginBottom: 12 }}>
                  <div><strong>Job:</strong> {detail.user.jobTitle || '—'}</div>
                  <div><strong>Dept:</strong> {detail.user.department || '—'}</div>
                  <div style={{ marginTop: 8 }}><StatusBadge enabled={detail.user.accountEnabled} /></div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginBottom: 6 }}>ADD TO GROUP</div>
                  <input
                    style={s.input}
                    placeholder="Search group name…"
                    value={groupSearch}
                    onChange={e => setGroupSearch(e.target.value)}
                  />
                  {groupOptions.length > 0 && (
                    <div style={{ marginTop: 6, border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
                      {groupOptions.map(g => (
                        <button
                          key={g.id}
                          type="button"
                          disabled={busy}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                            border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff', cursor: 'pointer', fontSize: 12,
                          }}
                          onClick={() => addToGroup(g)}
                        >
                          {g.displayName}{g.mail ? ` (${g.mail})` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {detail.groups?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginBottom: 6 }}>GROUPS ({detail.groups.length})</div>
                    <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                      {detail.groups.map(g => (
                        <div key={g.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, fontSize: 11, marginBottom: 4 }}>
                          <span style={{ color: '#4B5563', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.displayName}</span>
                          <button type="button" disabled={busy} style={{ ...s.btn('danger'), padding: '4px 8px', fontSize: 10 }} onClick={() => removeFromGroup(g)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {licenses.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginBottom: 6 }}>ASSIGN LICENSE</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select style={{ ...s.input, flex: 1 }} value={selectedLicense} onChange={e => setSelectedLicense(e.target.value)}>
                        <option value="">Select license…</option>
                        {licenses.map(l => (
                          <option key={l.skuId} value={l.skuId}>{l.skuPartNumber}</option>
                        ))}
                      </select>
                      <button type="button" disabled={busy} style={s.btn('warn')} onClick={assignLicense}>Request</button>
                    </div>
                  </div>
                )}
              </>
            ) : null}

            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginBottom: 8, textTransform: 'uppercase' }}>Request action</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('RESET_PASSWORD', { userKey: email, email }, 'Password reset')}>
                Password reset
              </button>
              <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('ENABLE_VPN_ACCESS', { email, userKey: email }, 'VPN access')}>
                VPN access
              </button>
              <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('REVOKE_SESSIONS', { userKey: email }, 'Revoke sessions')}>
                Revoke sign-in sessions
              </button>
              <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('DISABLE_USER', { userKey: email }, 'Disable account')}>
                Disable account
              </button>
              <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('ENABLE_USER', { userKey: email }, 'Enable account')}>
                Enable account
              </button>
              <button type="button" disabled={busy} style={s.btn('danger')} onClick={() => runAction('OFFBOARD_USER', { userKey: email, email }, 'Offboard user')}>
                Offboard user
              </button>
            </div>
            <button type="button" style={{ ...s.btn(), marginTop: 12, width: '100%' }} onClick={() => { setSelected(null); setDetail(null); }}>
              Close
            </button>
          </div>
        )}
      </div>

      {modal === 'create' && (
        <CreateUserModal scope={scope} onClose={() => setModal(null)} onSubmitted={() => { setModal(null); load(); }} />
      )}
      {modal === 'onboard' && (
        <OnboardUserModal scope={scope} licenses={licenses} onClose={() => setModal(null)} onSubmitted={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}
